import { Box, IconButton, makeStyles } from '@material-ui/core'
import { DatePicker, DatePickerView } from '@material-ui/pickers'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'
import React from 'react'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { TimeSpan } from './ChartTimeSpanSelect'

// Note: A lot of this is similar to code in /form/ControlledHourPicker.tsx
// To merge shared code, several changes are required:
// - extend TimeSpan to include TimeSpan.Hour
// - filter TimeSpan.Hour away from charts
// - create TimePicker.tsx in /form-folder with most of the code in this file
// - make sure TimePicker supports all previous functionality in ControlledHourPicker
// - pass specific properties from ControlledHourPicker
// - make sure theming does not break for either use case

const useStyles = makeStyles((theme) => ({
  datePicker: {
    width: theme.spacing(15),
  },
  iconButton: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
}))

type Props = {
  timeSpan: TimeSpan
  selectedDate: Date
  onChange: (value: Date) => void
  startOfYearDate: Date
}

const timeSpanToDatePickerViewMap: Record<TimeSpan, DatePickerView[]> = {
  [TimeSpan.Year]: ['year'],
  [TimeSpan.Month]: ['month'],
  [TimeSpan.Day]: ['date'],
}

const timeSpanToIncrementFunctionMap: Record<TimeSpan, (date: Date) => Date> = {
  [TimeSpan.Year]: (date: Date) => addYears(date, 1),
  [TimeSpan.Month]: (date: Date) => addMonths(date, 1),
  [TimeSpan.Day]: (date: Date) => addDays(date, 1),
}

const timeSpanToDecrementFunctionMap: Record<TimeSpan, (date: Date) => Date> = {
  [TimeSpan.Year]: (date: Date) => subYears(date, 1),
  [TimeSpan.Month]: (date: Date) => subMonths(date, 1),
  [TimeSpan.Day]: (date: Date) => subDays(date, 1),
}

const timeSpanToDifferenceFunctionMap: Record<
  TimeSpan,
  (left: Date, right: Date) => number
> = {
  [TimeSpan.Year]: differenceInYears,
  [TimeSpan.Month]: differenceInMonths,
  [TimeSpan.Day]: differenceInDays,
}

const timeSpanToStepsInYearMap: Record<TimeSpan, number> = {
  [TimeSpan.Year]: 1,
  [TimeSpan.Month]: 12,
  [TimeSpan.Day]: 365,
}

const timeSpanToStartOfPeriodMap: Record<TimeSpan, (date: Date) => Date> = {
  [TimeSpan.Year]: startOfYear,
  [TimeSpan.Month]: startOfMonth,
  [TimeSpan.Day]: startOfDay,
}

export function getStartDateByTimeSpan(date: Date, timeSpan: TimeSpan): Date {
  const startOfPeriod = timeSpanToStartOfPeriodMap[timeSpan]
  return startOfPeriod(date)
}

export function ChartTimePicker({
  timeSpan,
  selectedDate,
  onChange,
  startOfYearDate,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()

  const handleChange = (date: MaterialUiPickersDate) => {
    if (!date) {
      return
    }

    onChange(getStartDateByTimeSpan(date, timeSpan))
  }

  const incrementByTimeSpan = timeSpanToIncrementFunctionMap[timeSpan]
  const decrementByTimeSpan = timeSpanToDecrementFunctionMap[timeSpan]
  const differenceByTimeSpan = timeSpanToDifferenceFunctionMap[timeSpan]
  const stepsInYearByTimeSpan = timeSpanToStepsInYearMap[timeSpan]

  const incrementTime = () => {
    onChange(incrementByTimeSpan(selectedDate))
  }

  const decrementTime = () => {
    onChange(decrementByTimeSpan(selectedDate))
  }

  const incrementDisabled =
    differenceByTimeSpan(incrementByTimeSpan(selectedDate), startOfYearDate) >=
    stepsInYearByTimeSpan

  const decrementDisabled =
    differenceByTimeSpan(decrementByTimeSpan(selectedDate), startOfYearDate) < 0

  return (
    <Box display="flex" flexDirection="row">
      <DatePicker
        views={timeSpanToDatePickerViewMap[timeSpan]}
        label={t(`charts.timePickerLabelByTimeSpan.${timeSpan}`)}
        disabled={timeSpan === TimeSpan.Year}
        value={selectedDate}
        onChange={handleChange}
        format={t(`date.selectLabelByTimeSpan.${timeSpan}`)}
        className={classes.datePicker}
      />

      <IconButton
        className={classes.iconButton}
        onClick={decrementTime}
        disabled={decrementDisabled}
      >
        <ChevronLeft />
      </IconButton>

      <IconButton
        className={classes.iconButton}
        onClick={incrementTime}
        disabled={incrementDisabled}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  )
}
